/**
 * Sanitizador de Dados para Logs
 * 
 * Centraliza a sanitização de dados sensíveis em logs, removendo ou
 * mascarando informações como senhas, tokens, credenciais, etc.
 * 
 * @module log.sanitizer
 */

/**
 * Lista de campos sensíveis que devem ser mascarados nos logs
 */
export const SENSITIVE_FIELDS = [
  'password', 'senha', 'token', 'authorization', 'auth', 'secret', 'key',
  'apikey', 'api_key', 'access_token', 'refresh_token', 'jwt', 'bearer',
  'credential', 'credentials', 'private', 'confidential', 'ssn', 'cpf',
  'cnpj', 'email', 'phone', 'telefone', 'endereco', 'address',
  'sessiontoken', 'session_token', 'hmackey', 'hmac_key', 'signature',
  'nonce', 'cryptokey', 'crypto_key'
];

/**
 * Campos que são permitidos em logs de debug HMAC
 * Estes campos não serão sanitizados quando em modo DEBUG para permitir debugging
 */
export const HMAC_DEBUG_ALLOWED_FIELDS = [
  'idempotencykey', 'nonce', 'signature', 'timestamp', 'canonicalbody',
  'payload', 'hmac', 'requestid'
];

/**
 * Verifica se um valor parece ser um token/senha (heurística)
 * 
 * @param value - Valor a ser verificado
 * @returns true se o valor parece ser sensível
 */
function looksLikeSensitiveValue(value: any): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  
  const str = String(value);
  
  // Tokens geralmente são longos e contêm caracteres alfanuméricos e especiais
  if (str.length > 20 && /^[A-Za-z0-9+/=_-]+$/.test(str)) {
    return true;
  }
  
  // Base64-like strings
  if (str.length > 16 && /^[A-Za-z0-9+/=]+$/.test(str) && str.length % 4 === 0) {
    return true;
  }
  
  return false;
}

/**
 * Sanitiza dados removendo ou mascarando informações sensíveis
 * 
 * Remove ou mascara:
 * - Campos com nomes sensíveis (password, token, etc.)
 * - Valores que parecem ser tokens/senhas (heurística)
 * - Objetos aninhados são sanitizados recursivamente
 * 
 * @param data - Dados a serem sanitizados
 * @param depth - Profundidade atual na recursão (limite para evitar loops infinitos)
 * @param allowHmacDebug - Se true, permite campos de debug HMAC (apenas nível DEBUG)
 * @returns Dados sanitizados com campos sensíveis mascarados como '[REDACTED]'
 * 
 * @example
 * ```typescript
 * sanitizeLogData({ password: 'secret123', name: 'John' })
 * // { password: '[REDACTED]', name: 'John' }
 * 
 * sanitizeLogData({ token: 'abc123...', nested: { key: 'value' } })
 * // { token: '[REDACTED]', nested: { key: 'value' } }
 * ```
 */
export function sanitizeLogData(
  data: any,
  depth: number = 0,
  allowHmacDebug: boolean = false
): any {
  // Limite de profundidade para evitar loops infinitos
  if (depth > 10) {
    return '[MAX_DEPTH]';
  }

  // Valores primitivos
  if (!data || typeof data !== 'object') {
    // Verificar se o valor em si parece ser sensível
    if (!allowHmacDebug && looksLikeSensitiveValue(data)) {
      return '[REDACTED]';
    }
    return data;
  }

  // Arrays
  if (Array.isArray(data)) {
    return data.map((item) => sanitizeLogData(item, depth + 1, allowHmacDebug));
  }

  // Objetos
  const sanitized: any = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();

    // Se allowHmacDebug = true e campo é permitido para HMAC debug, não sanitizar
    const isHmacDebugAllowed =
      allowHmacDebug &&
      HMAC_DEBUG_ALLOWED_FIELDS.some((field) => lowerKey.includes(field));

    if (isHmacDebugAllowed) {
      // Campo permitido em debug HMAC - não sanitizar
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitizeLogData(value, depth + 1, allowHmacDebug);
      } else {
        sanitized[key] = value;
      }
    } else {
      // Lógica normal de sanitização
      const isSensitive = SENSITIVE_FIELDS.some((field) =>
        lowerKey.includes(field)
      );

      if (isSensitive) {
        sanitized[key] = '[REDACTED]';
      } else if (looksLikeSensitiveValue(value)) {
        // Valor parece ser sensível mesmo sem campo explícito
        sanitized[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitizeLogData(value, depth + 1, allowHmacDebug);
      } else {
        sanitized[key] = value;
      }
    }
  }

  return sanitized;
}

